import {
	cloneElement,
	Component,
	createRef,
	CSSProperties,
	HTMLAttributes,
	ReactElement,
	ReactNode,
} from "react"
import { debounce, throttle } from "lodash"

const isIntersectionObserverAvailable = (): boolean =>
	typeof window !== "undefined" &&
	"IntersectionObserver" in window &&
	"IntersectionObserverEntry" in window &&
	"isIntersecting" in window.IntersectionObserverEntry.prototype

const getElementOverflowValues = (element: HTMLElement): string => {
	const computedStyle = getComputedStyle(element)
	return (
		computedStyle.getPropertyValue("overflow") +
		computedStyle.getPropertyValue("overflow-y") +
		computedStyle.getPropertyValue("overflow-x")
	)
}

const getScrollAncestor = (element: Node | null): HTMLElement | Window => {
	if (!element || !(element instanceof HTMLElement)) {
		return window
	}
	let parent: Node | null = element
	while (parent) {
		if (!(parent instanceof HTMLElement)) {
			break
		}
		if (/(scroll|auto)/.test(getElementOverflowValues(parent))) {
			return parent
		}
		parent = parent.parentNode
	}
	return window
}

export type ScrollPosition = { x: number; y: number } | null
export type DelayMethod = "debounce" | "throttle"

type PlaceholderWithoutTrackingProps = {
	onVisible: () => void
	className?: string
	height?: number | string
	placeholder?: ReactElement | null
	scrollPosition?: ScrollPosition
	style?: CSSProperties
	threshold?: number
	useIntersectionObserver?: boolean
	width?: number | string
}

type ObserverTarget = Element & { onVisible?: () => void }

const LAZY_LOAD_OBSERVERS: Record<string, IntersectionObserver> = {}

const checkIntersections = (entries: IntersectionObserverEntry[]) => {
	entries.forEach((entry) => {
		if (entry.isIntersecting) {
			const target = entry.target as ObserverTarget
			target.onVisible?.()
		}
	})
}

const getObserver = (threshold: number): IntersectionObserver => {
	const key = String(threshold)
	LAZY_LOAD_OBSERVERS[key] =
		LAZY_LOAD_OBSERVERS[key] ||
		new IntersectionObserver(checkIntersections, {
			rootMargin: threshold + "px",
		})
	return LAZY_LOAD_OBSERVERS[key]
}

class PlaceholderWithoutTracking extends Component<PlaceholderWithoutTrackingProps> {
	static defaultProps = {
		className: "",
		placeholder: null,
		threshold: 100,
		useIntersectionObserver: true,
	}

	supportsObserver: boolean
	observer?: IntersectionObserver
	placeholder: ObserverTarget | null = null

	constructor(props: PlaceholderWithoutTrackingProps) {
		super(props)
		this.supportsObserver =
			!props.scrollPosition &&
			!!props.useIntersectionObserver &&
			isIntersectionObserverAvailable()
		if (this.supportsObserver) {
			this.observer = getObserver(props.threshold ?? 100)
		}
	}

	componentDidMount() {
		if (this.placeholder && this.observer) {
			this.placeholder.onVisible = this.props.onVisible
			this.observer.observe(this.placeholder)
		}
		if (!this.supportsObserver) {
			this.updateVisibility()
		}
	}

	componentWillUnmount() {
		if (this.observer && this.placeholder) {
			this.observer.unobserve(this.placeholder)
		}
	}

	componentDidUpdate() {
		if (!this.supportsObserver) {
			this.updateVisibility()
		}
	}

	getPlaceholderBoundingBox(
		scrollPosition: ScrollPosition = this.props.scrollPosition ?? {
			x: 0,
			y: 0,
		},
	) {
		if (!this.placeholder || !scrollPosition) {
			return { bottom: 0, left: 0, right: 0, top: 0 }
		}
		const boundingRect = this.placeholder.getBoundingClientRect()
		const style = (this.placeholder as HTMLElement).style
		const margin = {
			left: parseInt(style.getPropertyValue("margin-left"), 10) || 0,
			top: parseInt(style.getPropertyValue("margin-top"), 10) || 0,
		}
		return {
			bottom: scrollPosition.y + boundingRect.bottom + margin.top,
			left: scrollPosition.x + boundingRect.left + margin.left,
			right: scrollPosition.x + boundingRect.right + margin.left,
			top: scrollPosition.y + boundingRect.top + margin.top,
		}
	}

	isPlaceholderInViewport(): boolean {
		if (typeof window === "undefined" || !this.placeholder) {
			return false
		}
		const scrollPosition = this.props.scrollPosition ?? { x: 0, y: 0 }
		const threshold = this.props.threshold ?? 100
		const boundingBox = this.getPlaceholderBoundingBox(scrollPosition)
		const viewport = {
			bottom: scrollPosition.y + window.innerHeight,
			left: scrollPosition.x,
			right: scrollPosition.x + window.innerWidth,
			top: scrollPosition.y,
		}
		return Boolean(
			viewport.top - threshold <= boundingBox.bottom &&
				viewport.bottom + threshold >= boundingBox.top &&
				viewport.left - threshold <= boundingBox.right &&
				viewport.right + threshold >= boundingBox.left,
		)
	}

	updateVisibility() {
		if (this.isPlaceholderInViewport()) {
			this.props.onVisible()
		}
	}

	render() {
		const { className, height, placeholder, style, width } = this.props
		const setRef = (el: ObserverTarget | null) => {
			this.placeholder = el
		}
		if (placeholder && typeof placeholder.type !== "function") {
			return cloneElement(placeholder, {
				ref: setRef,
			} as Partial<HTMLAttributes<HTMLElement>>)
		}
		const styleProp: CSSProperties = {
			display: "inline-block",
			...style,
		}
		if (typeof width !== "undefined") styleProp.width = width
		if (typeof height !== "undefined") styleProp.height = height
		return (
			<span className={className} ref={setRef} style={styleProp}>
				{placeholder}
			</span>
		)
	}
}

type TrackedProps = PlaceholderWithoutTrackingProps & {
	delayMethod?: DelayMethod
	delayTime?: number
}

class PlaceholderWithTracking extends Component<TrackedProps> {
	static defaultProps = {
		delayMethod: "throttle" as DelayMethod,
		delayTime: 300,
		useIntersectionObserver: true,
	}

	useIntersectionObserver: boolean
	state: { scrollPosition: { x: number; y: number } }
	delayedScroll?: () => void
	baseComponentRef = createRef<HTMLElement>()
	scrollElement?: HTMLElement | Window

	constructor(props: TrackedProps) {
		super(props)
		this.useIntersectionObserver =
			!!props.useIntersectionObserver && isIntersectionObserverAvailable()
		this.state = {
			scrollPosition: {
				x:
					typeof window === "undefined"
						? 0
						: window.scrollX || window.pageXOffset,
				y:
					typeof window === "undefined"
						? 0
						: window.scrollY || window.pageYOffset,
			},
		}
		if (this.useIntersectionObserver) {
			return
		}
		const onChangeScroll = this.onChangeScroll.bind(this)
		const delayMethod = props.delayMethod ?? "throttle"
		const delayTime = props.delayTime ?? 300
		if (delayMethod === "debounce") {
			this.delayedScroll = debounce(onChangeScroll, delayTime)
		} else {
			this.delayedScroll = throttle(onChangeScroll, delayTime)
		}
	}

	componentDidMount() {
		this.addListeners()
	}

	componentWillUnmount() {
		this.removeListeners()
	}

	componentDidUpdate() {
		if (typeof window === "undefined" || this.useIntersectionObserver) {
			return
		}
		const scrollElement = getScrollAncestor(this.baseComponentRef.current)
		if (scrollElement !== this.scrollElement) {
			this.removeListeners()
			this.addListeners()
		}
	}

	addListeners() {
		if (
			typeof window === "undefined" ||
			this.useIntersectionObserver ||
			!this.delayedScroll
		) {
			return
		}
		this.scrollElement = getScrollAncestor(this.baseComponentRef.current)
		this.scrollElement.addEventListener("scroll", this.delayedScroll, {
			passive: true,
		})
		window.addEventListener("resize", this.delayedScroll, {
			passive: true,
		})
		if (this.scrollElement !== window) {
			window.addEventListener("scroll", this.delayedScroll, {
				passive: true,
			})
		}
	}

	removeListeners() {
		if (
			typeof window === "undefined" ||
			this.useIntersectionObserver ||
			!this.delayedScroll ||
			!this.scrollElement
		) {
			return
		}
		this.scrollElement.removeEventListener(
			"scroll",
			this.delayedScroll as EventListener,
		)
		window.removeEventListener(
			"resize",
			this.delayedScroll as EventListener,
		)
		if (this.scrollElement !== window) {
			window.removeEventListener(
				"scroll",
				this.delayedScroll as EventListener,
			)
		}
	}

	onChangeScroll() {
		if (this.useIntersectionObserver) {
			return
		}
		this.setState({
			scrollPosition: {
				x: window.scrollX || window.pageXOffset,
				y: window.scrollY || window.pageYOffset,
			},
		})
	}

	render() {
		const {
			delayMethod: _delayMethod,
			delayTime: _delayTime,
			...rest
		} = this.props
		void _delayMethod
		void _delayTime
		const scrollPosition = this.useIntersectionObserver
			? null
			: this.state.scrollPosition
		return (
			<PlaceholderWithoutTracking
				{...rest}
				scrollPosition={scrollPosition}
			/>
		)
	}
}

export type LazyLoadComponentProps = {
	afterLoad?: () => void
	beforeLoad?: () => void
	className?: string
	delayMethod?: DelayMethod
	delayTime?: number
	height?: number | string
	placeholder?: ReactElement | null
	scrollPosition?: ScrollPosition
	style?: CSSProperties
	threshold?: number
	useIntersectionObserver?: boolean
	visibleByDefault?: boolean
	width?: number | string
	children?: ReactNode
}

export class LazyLoadComponent extends Component<
	LazyLoadComponentProps,
	{ visible: boolean }
> {
	static defaultProps: Partial<LazyLoadComponentProps> = {
		afterLoad: () => ({}),
		beforeLoad: () => ({}),
		useIntersectionObserver: true,
		visibleByDefault: false,
	}

	isScrollTracked: boolean

	constructor(props: LazyLoadComponentProps) {
		super(props)
		const { afterLoad, beforeLoad, scrollPosition, visibleByDefault } =
			props
		this.state = { visible: !!visibleByDefault }
		if (visibleByDefault) {
			beforeLoad?.()
			afterLoad?.()
		}
		this.onVisible = this.onVisible.bind(this)
		this.isScrollTracked = Boolean(
			scrollPosition &&
				Number.isFinite(scrollPosition.x) &&
				scrollPosition.x >= 0 &&
				Number.isFinite(scrollPosition.y) &&
				scrollPosition.y >= 0,
		)
	}

	componentDidUpdate(
		_prevProps: LazyLoadComponentProps,
		prevState: { visible: boolean },
	) {
		if (prevState.visible !== this.state.visible) {
			this.props.afterLoad?.()
		}
	}

	onVisible() {
		this.props.beforeLoad?.()
		this.setState({ visible: true })
	}

	render() {
		if (this.state.visible) {
			return this.props.children
		}
		const {
			className,
			delayMethod,
			delayTime,
			height,
			placeholder,
			scrollPosition,
			style,
			threshold,
			useIntersectionObserver,
			width,
		} = this.props
		if (
			this.isScrollTracked ||
			(useIntersectionObserver && isIntersectionObserverAvailable())
		) {
			return (
				<PlaceholderWithoutTracking
					className={className}
					height={height}
					onVisible={this.onVisible}
					placeholder={placeholder}
					scrollPosition={scrollPosition}
					style={style}
					threshold={threshold}
					useIntersectionObserver={useIntersectionObserver}
					width={width}
				/>
			)
		}
		return (
			<PlaceholderWithTracking
				className={className}
				delayMethod={delayMethod}
				delayTime={delayTime}
				height={height}
				onVisible={this.onVisible}
				placeholder={placeholder ?? null}
				style={style}
				threshold={threshold}
				width={width}
			/>
		)
	}
}

export default LazyLoadComponent
